<template>
  <div>
    <v-overlay :value="loading" :z-index="9999" opacity="0.9">
      <v-row class="fill-height" align="center" justify="center" style="flex-direction: column; text-align: center">
        <v-progress-circular indeterminate size="70" color="primary" class="mb-4" />
        <div class="text-h6 white--text">
          Anfrage wird bearbeitet! Upload erfolgt! Bitte warten...
        </div>
      </v-row>
    </v-overlay>
    <v-container>
      <v-row>
        <v-col cols="12" sm="3" order="2" order-sm="1" class="text-center text-sm-left">
          <v-btn @click="toShopView" color="success" :block="$vuetify.breakpoint.xsOnly">Zum Shop</v-btn>
        </v-col>
        <v-col cols="12" sm="6" order="1" order-sm="2" class="text-center">
          <v-btn x-large color="success" :block="$vuetify.breakpoint.xsOnly" @click="openDialog({
            name: '',
            type: 'Normal',
            rarity: 1,
            description: '',
            icon: null,
            cost: 0,
            enabled_in_shop: 0,
            chat_msg: 0,
            effects: [],
            edit: 0,
          })">Rune erstellen</v-btn>
        </v-col>
        <v-col cols="12" sm="3" order="3" class="text-center text-sm-right">
          <v-btn @click="toggleText" color="success" :block="$vuetify.breakpoint.xsOnly">How To</v-btn>
        </v-col>
      </v-row>
    </v-container>
    <div class="channelPointsWrapper" :class="{ drawerOpen: !$store.state.navigation_drawer_mini }">
      <v-row align="stretch">
        <v-col v-for="item in sortedRewards" :key="item.id" cols="12" sm="6" md="4" lg="4" class="d-flex">
          <v-card outlined class="d-flex flex-column rune-card">
            <div class="d-flex align-center pa-4 pb-0">
              <img
                class="rune-card-image mr-3"
                :src="item.icon ? getItemImage(item.icon) : getDefaultRuneImage(item)"
                alt="Rune"
              >
              <div class="flex-grow-1" style="min-width: 0">
                <div class="text-h6 text-truncate">{{ item.name }}</div>
                <div class="text-caption grey--text">
                  Runengrad {{ item.rarity }} &middot; {{ item.cost }} Kosten
                </div>
              </div>
            </div>

            <v-card-text class="flex-grow-1">
              <div class="font-weight-bold mb-2">Runen Effekte:</div>
              <div
                v-for="effect in item.effects"
                :key="effect.id"
                class="mb-2 pa-2 rounded"
                style="background: rgba(0,0,0,0.05);"
              >
                <div class="font-weight-bold text-subtitle-2">
                  {{ effect.name }}
                </div>

                <div
                  v-for="attr in getVisibleAttributes(effect)"
                  :key="attr.index"
                  class="text-body-2"
                >
                  {{ attr.label }}: {{ formatAttr(effect, attr.index) }}
                </div>
              </div>

              <div class="font-weight-bold mt-3 mb-1">Beschreibung:</div>
              <div class="text-body-2" style="white-space: pre-wrap">{{ item.description }}</div>
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn :color="item.is_bot_owner === 0 ? 'gray' : 'primary'" dark @click="openDialog(item)">
                Rune bearbeiten
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <v-dialog v-model="showText" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card>
        <v-toolbar dark color="primary">
          <v-btn icon dark @click="showText = !showText">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-title class="text-h5 font-weight-bold">Runen Editor – How To</v-card-title>
        <v-card-text>

          <v-divider class="mb-4"></v-divider>

          <div class="text-h6 font-weight-bold mb-2">1. Runentypen</div>
          <p>
            Beim Erstellen einer Rune muss ein Runentyp gewählt werden. Dieser bestimmt, welche Effekte verfügbar sind
            und welche Obergrenzen (Caps) für die Gesamt-Rarität gelten.
            <strong>Achtung: Der Runentyp kann nach dem Erstellen nicht mehr geändert werden.</strong>
          </p>
          <v-simple-table class="mb-4">
            <thead>
              <tr>
                <th>Typ</th>
                <th>Beschreibung</th>
                <th>Cap (Maximum)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Normal</strong></td>
                <td>Standard-Runen für den regulären Shop</td>
                <td>100 %</td>
              </tr>
              <tr>
                <td><strong>Bonus</strong></td>
                <td>Runen mit leicht erhöhten Effektstärken, z.B. für Sub-Boni</td>
                <td>125 %</td>
              </tr>
              <tr>
                <td><strong>Event</strong></td>
                <td>Mächtige Runen für besondere Events oder saisonale Aktionen</td>
                <td>150 %</td>
              </tr>
            </tbody>
          </v-simple-table>

          <v-divider class="mb-4"></v-divider>

          <div class="text-h6 font-weight-bold mb-2">2. Gesamt-Rarität & Caps</div>
          <p>
            Die <strong>Gesamt-Rarität</strong> einer Rune errechnet sich automatisch aus den gewählten Effekten und deren Slider-Werten.
            Je stärker die Effekte eingestellt sind, desto höher der Prozentwert.
            Anhand dieses Wertes wird die <strong>Runenstufe</strong> automatisch bestimmt:
          </p>
          <v-simple-table class="mb-2">
            <thead>
              <tr>
                <th>Typ</th>
                <th>Stufe 1</th>
                <th>Stufe 2</th>
                <th>Stufe 3</th>
                <th>Ungültig (nicht speicherbar)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Normal</strong></td>
                <td>0 – 24,9 %</td>
                <td>25 – 49,9 %</td>
                <td>50 – 100 %</td>
                <td>&gt; 100 %</td>
              </tr>
              <tr>
                <td><strong>Bonus</strong></td>
                <td>0 – 34,9 %</td>
                <td>35 – 74,9 %</td>
                <td>75 – 125 %</td>
                <td>&gt; 125 %</td>
              </tr>
              <tr>
                <td><strong>Event</strong></td>
                <td>0 – 49,9 %</td>
                <td>50 – 99,9 %</td>
                <td>100 – 150 %</td>
                <td>&gt; 150 %</td>
              </tr>
            </tbody>
          </v-simple-table>
          <p class="mb-4">
            Wird der Cap überschritten, wird die Rune als <strong style="color:red">❌ Ungültig</strong> markiert und kann
            nicht gespeichert werden. Die aktuelle Gesamt-Rarität und Stufe wird im Editor direkt oberhalb der Effekte angezeigt.
          </p>

          <v-divider class="mb-4"></v-divider>

          <div class="text-h6 font-weight-bold mb-2">3. Maximale Effekte pro Rune</div>
          <p class="mb-4">
            Jede Rune kann mit <strong>maximal 3 Effekten</strong> ausgestattet werden. Das Dropdown zur Effektauswahl
            sowie der „Effekt hinzufügen"-Button werden automatisch deaktiviert, sobald 3 Effekte vergeben sind.
            Derselbe Effekttyp kann einer Rune nur <strong>einmal</strong> zugewiesen werden – doppelte Effekte sind nicht erlaubt.
          </p>

          <v-divider class="mb-4"></v-divider>

          <div class="text-h6 font-weight-bold mb-2">4. Standard-Runenbilder</div>
          <p>
            Für jeden Runentyp und jede Runenstufe gibt es ein vordefiniertes Standard-Bild.
            Wird kein eigenes Bild hochgeladen, wird dieses Standard-Bild automatisch verwendet.
            Eigene Bilder können jederzeit über den Datei-Upload gesetzt oder mit „Bild löschen" entfernt werden,
            woraufhin das Standard-Bild wieder greift.
          </p>
          <v-row class="mb-4 mt-2">
            <v-col cols="12">
              <div class="font-weight-bold mb-2">Normal-Runen:</div>
            </v-col>
            <v-col cols="6" sm="4" class="text-center">
              <img src="/img/forge_rune/Runestone_Normal_1.png" style="width:80px;height:80px;object-fit:contain;" alt="Normal Stufe 1" />
              <div class="text-caption mt-1">Normal – Stufe 1</div>
            </v-col>
            <v-col cols="6" sm="4" class="text-center">
              <img src="/img/forge_rune/Runestone_Normal_2.png" style="width:80px;height:80px;object-fit:contain;" alt="Normal Stufe 2" />
              <div class="text-caption mt-1">Normal – Stufe 2</div>
            </v-col>
            <v-col cols="6" sm="4" class="text-center">
              <img src="/img/forge_rune/Runestone_Normal_3.png" style="width:80px;height:80px;object-fit:contain;" alt="Normal Stufe 3" />
              <div class="text-caption mt-1">Normal – Stufe 3</div>
            </v-col>
            <v-col cols="12">
              <div class="font-weight-bold mb-2 mt-3">Bonus-Runen:</div>
            </v-col>
            <v-col cols="6" sm="4" class="text-center">
              <img src="/img/forge_rune/Runestone_Bonus_1.png" style="width:80px;height:80px;object-fit:contain;" alt="Bonus Stufe 1" />
              <div class="text-caption mt-1">Bonus – Stufe 1</div>
            </v-col>
            <v-col cols="6" sm="4" class="text-center">
              <img src="/img/forge_rune/Runestone_Bonus_2.png" style="width:80px;height:80px;object-fit:contain;" alt="Bonus Stufe 2" />
              <div class="text-caption mt-1">Bonus – Stufe 2</div>
            </v-col>
            <v-col cols="6" sm="4" class="text-center">
              <img src="/img/forge_rune/Runestone_Bonus_3.png" style="width:80px;height:80px;object-fit:contain;" alt="Bonus Stufe 3" />
              <div class="text-caption mt-1">Bonus – Stufe 3</div>
            </v-col>
            <v-col cols="12">
              <div class="font-weight-bold mb-2 mt-3">Event-Runen:</div>
            </v-col>
            <v-col cols="6" sm="4" class="text-center">
              <img src="/img/forge_rune/Runestone_Event_1.png" style="width:80px;height:80px;object-fit:contain;" alt="Event Stufe 1" />
              <div class="text-caption mt-1">Event – Stufe 1</div>
            </v-col>
            <v-col cols="6" sm="4" class="text-center">
              <img src="/img/forge_rune/Runestone_Event_2.png" style="width:80px;height:80px;object-fit:contain;" alt="Event Stufe 2" />
              <div class="text-caption mt-1">Event – Stufe 2</div>
            </v-col>
            <v-col cols="6" sm="4" class="text-center">
              <img src="/img/forge_rune/Runestone_Event_3.png" style="width:80px;height:80px;object-fit:contain;" alt="Event Stufe 3" />
              <div class="text-caption mt-1">Event – Stufe 3</div>
            </v-col>
          </v-row>

          <v-divider class="mb-4"></v-divider>

          <div class="text-h6 font-weight-bold mb-2">5. Runen löschen – Automatische Rückerstattung</div>
          <p class="mb-4">
            Wird eine Rune aus dem System gelöscht, erhalten <strong>alle Viewer, die diese Rune besitzen</strong>,
            automatisch den Kaufpreis der Rune zurückerstattet. Die Rückerstattung erfolgt direkt beim Löschen –
            es ist keine manuelle Aktion erforderlich.
          </p>

          <v-divider class="mb-4"></v-divider>

          <div class="text-h6 font-weight-bold mb-2">6. Begrenzte Bearbeitbarkeit nach dem Erstellen</div>
          <p>
            Sobald eine Rune gespeichert und von Viewern erworben werden kann, sind aus Fairnessgründen
            nur noch bestimmte Felder bearbeitbar:
          </p>
          <v-row class="mb-4">
            <v-col cols="12" md="6">
              <v-card outlined class="pa-3 green darken-4">
                <div class="font-weight-bold mb-2">Noch bearbeitbar:</div>
                <ul>
                  <li>Name der Rune</li>
                  <li>Runenbild (hochladen / löschen)</li>
                  <li>Beschreibung</li>
                  <li>Sichtbarkeit im Runen Shop (ein/aus)</li>
                  <li>Chat-Benachrichtigung bei Einlösung (ein/aus)</li>
                </ul>
              </v-card>
            </v-col>
            <v-col cols="12" md="6">
              <v-card outlined class="pa-3 red darken-4">
                <div class="font-weight-bold mb-2">Nicht mehr änderbar:</div>
                <ul>
                  <li>Runentyp (Normal / Bonus / Event)</li>
                  <li>Kosten (Kaufpreis)</li>
                  <li>Effekte (weder hinzufügen noch entfernen)</li>
                  <li>Effekt-Sliderwerte</li>
                </ul>
              </v-card>
            </v-col>
          </v-row>
          <p>
            Diese Einschränkungen greifen automatisch, sobald eine Rune den Status „bearbeitet" erhält
            (d.h. bereits im System existiert). Neu erstellte Runen können vor dem ersten Speichern
            noch vollständig konfiguriert werden.
          </p>

        </v-card-text>
        </v-card>
    </v-dialog>

    <v-dialog v-model="showDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card>
        <v-form ref="form" v-model="formValid">
        <v-toolbar dark color="primary">
          <v-btn icon dark @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>Runen Editor</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn
              color="error"
              @click="deleteReward"
              :hidden="selectedReward.id === undefined"
              :icon="$vuetify.breakpoint.xsOnly"
            >
              <v-icon v-if="$vuetify.breakpoint.xsOnly">mdi-delete</v-icon>
              <span v-else>Delete</span>
            </v-btn>
            <v-btn
              color="success"
              @click="saveChanges"
              :disabled="this.selectedReward.rarity === -1"
              :icon="$vuetify.breakpoint.xsOnly"
            >
              <v-icon v-if="$vuetify.breakpoint.xsOnly">mdi-content-save</v-icon>
              <span v-else>Save</span>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-list three-line subheader>
          <v-list-item>
            <v-list-item-content>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="selectedReward.name" label="Name der Rune" outlined counter="45" maxLength="45"
                    required :disabled="selectedReward.is_bot_owner === 0"></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="selectedReward.cost" label="Kosten" outlined type="Number" :rules="rulesWith0" min="0" required
                    :disabled="selectedReward.is_bot_owner === 0 || selectedReward.edit === 1"></v-text-field>
                </v-col>
              </v-row>
            </v-list-item-content>
          </v-list-item>
          <v-list-item>
            <v-list-item-content>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-select v-model="selectedReward.type" item-text="title" :items="lr_types" item-value="title" label="Runentyp:" required @change="onTypeChange" :disabled="selectedReward.edit === 1"
                  ></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-file-input v-model="selectedReward.icon" label="Bild auswählen" truncate-length="15"
                    @change="onFileChange"></v-file-input>
                  <v-btn
                    v-if="selectedReward.icon !== null && selectedReward.icon !== '' && selectedReward.icon !== undefined"
                    color="red"
                    @click="
                      selectedReward.icon = null;
                    "
                    >Bild löschen</v-btn>
                </v-col>
              </v-row>
            </v-list-item-content>
          </v-list-item>
          <v-list-item>
            <v-list-item-content>
              <v-textarea v-model="selectedReward.description" label="Beschreibung" rows="1" auto-grow outlined counter="200"
                maxLength="200" :disabled="selectedReward.is_bot_owner === 0"></v-textarea>
            </v-list-item-content>
          </v-list-item>
          <v-list-item>
            <v-list-item-content>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-switch v-model="selectedReward.enabled_in_shop"
                    label="Rune ist sichtbar im Runen Shop" color="success" :true-value="1"
                    :false-value="0" hide-details @change="saveSwitchState(selectedReward)"
                    :disabled="selectedReward.is_bot_owner === 0"></v-switch>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-switch v-model="selectedReward.chat_msg" label="Ausgabe bei Einlösung im Chat" color="success"
                    :true-value="1" :false-value="0" hide-details @change="saveSwitchState(selectedReward)"
                    :disabled="selectedReward.is_bot_owner === 0"></v-switch>
                </v-col>
              </v-row>
            </v-list-item-content>
          </v-list-item>
          <v-list-item>
            <v-list-item-content>
              <h2>Effekte</h2>
              <div v-for="(effect, index) in selectedReward.effects" :key="effect.id" class="mb-2" style="border: 1px solid white">
                <h3 v-if="index === 0" class="pa-2">Gesamt-Rarität: {{ getTotalPercent().toFixed(1) }}% ({{ getRuneLevel() }})</h3>
                <v-divider></v-divider>
                <div class="d-flex flex-wrap align-center justify-space-between px-2 py-1">
                  <span class="font-weight-bold">{{ effect.name }}</span>
                  <v-btn @click="removeEffect(index)" color="error" small :disabled="selectedReward.edit === 1">Entfernen</v-btn>
                </div>
                <div v-if="effect.slug == 'monthlypoints'">
                  <v-divider></v-divider>
                    <h4 class="pa-2">Monthly Points Basiswert: {{ effect.attribute1min }} </h4>
                    <v-divider></v-divider>
                    <h4 class="pa-2">Monthly Points Bonuseffekt: {{ effect.attribute2 }}</h4>
                    <v-slider v-model="effect.attribute2" :min="effect.attribute2min" :max="effect.attribute2max" label="Monthly Bonus Effekt" class="mr-4" :disabled="selectedReward.edit === 1">
                    </v-slider>
                </div>
                <div v-if="effect.slug == 'loyaltypoints'">
                  <h4 class="pa-2">Loyalty Points Basiswert: {{ effect.attribute1min }} </h4>
                  <v-divider></v-divider>
                  <h4 class="pa-2">Loyalty Points Bonus: {{ effect.attribute2 }}</h4>
                    <v-slider v-model="effect.attribute2" :min="effect.attribute2min" :max="effect.attribute2max" label="Loyalty Bonus Effekt" class="mr-4" :disabled="selectedReward.edit === 1">
                    </v-slider>
                </div>
                <div v-if="effect.slug == 'xp'">
                  <v-divider></v-divider>
                  <h4 class="pa-2">XP Basiswert: {{ effect.attribute1min }} </h4>
                  <v-divider></v-divider>
                  <h4 class="pa-2">XP Bonus Wert: {{ effect.attribute2 }}</h4>
                    <v-slider v-model="effect.attribute2" :min="effect.attribute2min" :max="effect.attribute2max" label="XY" class="mr-4" :disabled="selectedReward.edit === 1">
                    </v-slider>
                </div>
                <div v-if="effect.slug == 'luck_loyalty'">
                  <v-divider></v-divider>
                  <h4 class="pa-2">Chance: {{ effect.attribute1 }}%</h4>
                  <v-slider v-model="effect.attribute1" :min="effect.attribute1min" :max="effect.attribute1max" label="XY" class="mr-4" :disabled="selectedReward.edit === 1">
                  </v-slider>
                  <v-divider></v-divider>
                  <h4 class="pa-2">Loyalty Points pro Chance: {{ effect.attribute2 }}</h4>
                    <v-slider v-model="effect.attribute2" :min="effect.attribute2min" :max="effect.attribute2max" label="XY" class="mr-4" :disabled="selectedReward.edit === 1">
                    </v-slider>
                </div>
                <div v-if="effect.slug == 'double_xp'">
                  <v-divider></v-divider>
                  <h4 class="pa-2">XP Basiswert: {{ effect.attribute2min }} </h4>
                  <v-divider></v-divider>
                  <h4 class="pa-2">Chance auf Doppelte XP: {{ effect.attribute1 }}%</h4>
                    <v-slider v-model="effect.attribute1" :min="effect.attribute1min" :max="effect.attribute1max" label="XY" class="mr-4" :disabled="selectedReward.edit === 1">
                    </v-slider>
                </div>
                <div v-if="effect.slug == 'double_xp_dono'">
                  <v-divider></v-divider>
                  <h4 class="pa-2">XP Basiswert: {{ effect.attribute2min }} </h4>
                  <v-divider></v-divider>
                  <h4 class="pa-2">Chance auf Doppelte XP bei einer Donation: {{ effect.attribute1 }}%</h4>
                    <v-slider v-model="effect.attribute1" :min="effect.attribute1min" :max="effect.attribute1max" label="XY" class="mr-4" :disabled="selectedReward.edit === 1">
                    </v-slider>
                </div>
                <div v-if="effect.slug == 'double_loyalty_dono'">
                  <v-divider></v-divider>
                  <h4 class="pa-2">Loyaltiy Points Basiswert: {{ effect.attribute2min }} </h4>
                  <v-divider></v-divider>
                  <h4 class="pa-2">Chance auf Doppelte Loyaltiy Points bei einer Donation: {{ effect.attribute1 }}%</h4>
                    <v-slider v-model="effect.attribute1" :min="effect.attribute1min" :max="effect.attribute1max" label="XY" class="mr-4" :disabled="selectedReward.edit === 1">
                    </v-slider>
                </div>
                <div v-if="effect.slug == 'double_monthly_dono'">
                  <v-divider></v-divider>
                  <h4 class="pa-2">Monthly Points  Basiswert: {{ effect.attribute2min }} </h4>
                  <v-divider></v-divider>
                  <h4 class="pa-2">Chance auf Doppelte Monthly Points bei einer Donation: {{ effect.attribute1 }}%</h4>
                    <v-slider v-model="effect.attribute1" :min="effect.attribute1min" :max="effect.attribute1max" label="XY" class="mr-4" :disabled="selectedReward.edit === 1">
                    </v-slider>
                </div>
                <div v-if="effect.slug == 'double_loyalty'">
                  <v-divider></v-divider>
                  <h4 class="pa-2">Loyalty Points Basiswert: {{ effect.attribute2min }} </h4>
                  <v-divider></v-divider>
                  <h4 class="pa-2">Chance auf Doppelte Loyalty Points: {{ effect.attribute1 }}%</h4>
                    <v-slider v-model="effect.attribute1" :min="effect.attribute1min" :max="effect.attribute1max" label="XY" class="mr-4" :disabled="selectedReward.edit === 1">
                    </v-slider>
                </div>
                <div v-if="effect.slug == 'double_monthly'">
                  <v-divider></v-divider>
                  <h4 class="pa-2">Monthly Points Basiswert: {{ effect.attribute2min }} </h4>
                  <v-divider></v-divider>
                  <h4 class="pa-2">Chance auf Doppelte Monthly Points: {{ effect.attribute1 }}%</h4>
                    <v-slider v-model="effect.attribute1" :min="effect.attribute1min" :max="effect.attribute1max" label="XY" class="mr-4" :disabled="selectedReward.edit === 1">
                    </v-slider>
                </div>
                <div v-if="effect.slug == 'bonus_tier1_loyalty'">
                  <h4 class="pa-2">Loyalty Points Basiswert: {{ effect.attribute1min }} </h4>
                  <v-divider></v-divider>
                  <h4 class="pa-2">Loyalty Points Sub Stufe 1 Bonus: {{ effect.attribute2 }}</h4>
                    <v-slider v-model="effect.attribute2" :min="effect.attribute2min" :max="effect.attribute2max" label="Loyalty Bonus Effekt" class="mr-4" :disabled="selectedReward.edit === 1">
                    </v-slider>
                </div>
                <div v-if="effect.slug == 'bonus_tier2_loyalty'">
                  <h4 class="pa-2">Loyalty Points Basiswert: {{ effect.attribute1min }} </h4>
                  <v-divider></v-divider>
                  <h4 class="pa-2">Loyalty Points Sub Stufe 2 Bonus: {{ effect.attribute2 }}</h4>
                    <v-slider v-model="effect.attribute2" :min="effect.attribute2min" :max="effect.attribute2max" label="Loyalty Bonus Effekt" class="mr-4" :disabled="selectedReward.edit === 1">
                    </v-slider>
                </div>
                <div v-if="effect.slug == 'bonus_tier3_loyalty'">
                  <h4 class="pa-2">Loyalty Points Basiswert: {{ effect.attribute1min }} </h4>
                  <v-divider></v-divider>
                  <h4 class="pa-2">Loyalty Points Sub Stufe 3 Bonus: {{ effect.attribute2 }}</h4>
                    <v-slider v-model="effect.attribute2" :min="effect.attribute2min" :max="effect.attribute2max" label="Loyalty Bonus Effekt" class="mr-4" :disabled="selectedReward.edit === 1">
                    </v-slider>
                </div>
                <div v-if="effect.slug == 'bonus_tier1_monthly'">
                  <v-divider></v-divider>
                    <h4 class="pa-2">Monthly Points Basiswert: {{ effect.attribute1min }} </h4>
                    <v-divider></v-divider>
                    <h4 class="pa-2">Monthly Points Sub Stufe 1 Bonuseffekt: {{ effect.attribute2 }}</h4>
                    <v-slider v-model="effect.attribute2" :min="effect.attribute2min" :max="effect.attribute2max" label="Monthly Bonus Effekt" class="mr-4" :disabled="selectedReward.edit === 1">
                    </v-slider>
                </div>
                <div v-if="effect.slug == 'bonus_tier2_monthly'">
                  <v-divider></v-divider>
                    <h4 class="pa-2">Monthly Points Basiswert: {{ effect.attribute1min }} </h4>
                    <v-divider></v-divider>
                    <h4 class="pa-2">Monthly Points Sub Stufe 2 Bonuseffekt: {{ effect.attribute2 }}</h4>
                    <v-slider v-model="effect.attribute2" :min="effect.attribute2min" :max="effect.attribute2max" label="Monthly Bonus Effekt" class="mr-4" :disabled="selectedReward.edit === 1">
                    </v-slider>
                </div>
                <div v-if="effect.slug == 'bonus_tier3_monthly'">
                  <v-divider></v-divider>
                    <h4 class="pa-2">Monthly Points Basiswert: {{ effect.attribute1min }} </h4>
                    <v-divider></v-divider>
                    <h4 class="pa-2">Monthly Points Sub Stufe 3 Bonuseffekt: {{ effect.attribute2 }}</h4>
                    <v-slider v-model="effect.attribute2" :min="effect.attribute2min" :max="effect.attribute2max" label="Monthly Bonus Effekt" class="mr-4" :disabled="selectedReward.edit === 1">
                    </v-slider>
                </div>
                <div v-if="effect.slug == 'bonus_tier1_xp'">
                  <v-divider></v-divider>
                  <h4 class="pa-2">XP Basiswert: {{ effect.attribute1min }} </h4>
                  <v-divider></v-divider>
                  <h4 class="pa-2">XP Sub Stufe 1 Bonus: {{ effect.attribute2 }}</h4>
                    <v-slider v-model="effect.attribute2" :min="effect.attribute2min" :max="effect.attribute2max" label="XY" class="mr-4" :disabled="selectedReward.edit === 1">
                    </v-slider>
                </div>
                <div v-if="effect.slug == 'bonus_tier2_xp'">
                  <v-divider></v-divider>
                  <h4 class="pa-2">XP Basiswert: {{ effect.attribute1min }} </h4>
                  <v-divider></v-divider>
                  <h4 class="pa-2">XP Sub Stufe 2 Bonus: {{ effect.attribute2 }}</h4>
                    <v-slider v-model="effect.attribute2" :min="effect.attribute2min" :max="effect.attribute2max" label="XY" class="mr-4" :disabled="selectedReward.edit === 1">
                    </v-slider>
                </div>
                <div v-if="effect.slug == 'bonus_tier3_xp'">
                  <v-divider></v-divider>
                  <h4 class="pa-2">XP Basiswert: {{ effect.attribute1min }} </h4>
                  <v-divider></v-divider>
                  <h4 class="pa-2">XP Sub Stufe 3 Bonus: {{ effect.attribute2 }}</h4>
                    <v-slider v-model="effect.attribute2" :min="effect.attribute2min" :max="effect.attribute2max" label="XY" class="mr-4" :disabled="selectedReward.edit === 1">
                    </v-slider>
                </div>
                <div v-if="effect.slug == 'bonus_bits_loyalty'">
                  <h4 class="pa-2">Loyalty Points Basiswert: {{ effect.attribute1min }} </h4>
                  <v-divider></v-divider>
                  <h4 class="pa-2">Loyalty Points Bonus pro 100 Bits: {{ effect.attribute2 }}</h4>
                    <v-slider v-model="effect.attribute2" :min="effect.attribute2min" :max="effect.attribute2max" label="Loyalty Bonus Effekt" class="mr-4" :disabled="selectedReward.edit === 1">
                    </v-slider>
                </div>
                <div v-if="effect.slug == 'bonus_bits_monthly'">
                  <v-divider></v-divider>
                    <h4 class="pa-2">Monthly Points Basiswert: {{ effect.attribute1min }} </h4>
                    <v-divider></v-divider>
                    <h4 class="pa-2">Monthly Points Bonus pro 100 Bits: {{ effect.attribute2 }}</h4>
                    <v-slider v-model="effect.attribute2" :min="effect.attribute2min" :max="effect.attribute2max" label="Monthly Bonus Effekt" class="mr-4" :disabled="selectedReward.edit === 1">
                    </v-slider>
                </div>
                <div v-if="effect.slug == 'bonus_bit_xp'">
                  <v-divider></v-divider>
                  <h4 class="pa-2">XP Basiswert: {{ effect.attribute1min }} </h4>
                  <v-divider></v-divider>
                  <h4 class="pa-2">XP Bonus pro 100 Bits: {{ effect.attribute2 }}</h4>
                    <v-slider v-model="effect.attribute2" :min="effect.attribute2min" :max="effect.attribute2max" label="XY" class="mr-4" :disabled="selectedReward.edit === 1">
                    </v-slider>
                </div>
              </div>

            </v-list-item-content>
          </v-list-item>
          <v-list-item>
            <v-list-item-content>
              <v-select v-if="selectedReward.effects?.length < 3" v-model="selectedEffectType" :items="rune_effects.filter((e) => e.type === selectedReward.type && e.attribute1max > 0)" item-text="name" item-value="id" label="Effekttyp" :disabled="selectedReward.edit === 1"></v-select>
              <v-btn @click="addEffect" :disabled="!selectedEffectType || selectedReward.effects.length > 2">Effekt hinzufügen</v-btn>
            </v-list-item-content>
          </v-list-item>
        </v-list>
        </v-form>
      </v-card>
      <v-snackbar
      v-model="ErrorFlag"
      timeout="3000"
      color="red"
    >
      Überprüfe die roten Felder!

      <template v-slot:actions>
        <v-btn
          color="pink"
          variant="text"
          @click="ErrorFlag = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
    <v-snackbar
      v-model="ErrorFlagEffect"
      timeout="4000"
      color="red"
    >
      Du darfst eine Rune nicht zweimal den selben Effekt zuordnen!

      <template v-slot:actions>
        <v-btn
          color="pink"
          variant="text"
          @click="ErrorFlag = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
    </v-dialog>

    <v-dialog v-model="showNotification" max-width="600" transition="dialog-bottom-transition">
    </v-dialog>
    <v-dialog v-model="dialogDelete" max-width="600px">
      <v-card>
        <v-card-title class="text-h5">Bist du dir sicher, dass diese
          Rune gelöscht werden soll?</v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="closeDelete">Nein!</v-btn>
          <v-btn color="blue darken-1" text @click="deleteItemConfirm">Ja!</v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-snackbar v-model="errorReedemReward" timeout="5000" color="red">
      {{ dynamicErrorMsg }}

      <template v-slot:actions>
        <v-btn color="pink" variant="text" @click="errorReedemReward = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import BackendService from '@/services/BackendService';
import { getUnit } from '@/utils/runeUnits';

export default {
  data() {
    return {
      dialogDelete: false,
      showDialog: false,
      selectedIndex: 0,
      reward: [],
      errorReedemReward: false,
      dynamicErrorMsg: '',
      rune_effects: [],
      selectedEffectType: null,
      selectedReward: {},
      showNotification: false,
      loading: false,
      previousFile: null,
      fileChanged: false,
      fileWasCleared: false,
      fileInitiallySelected: false,
      previousSound: null,
      soundChanged: false,
      soundWasCleared: false,
      soundInitiallySelected: false,
      showText: false,
      baseUrl: '',
      cmdlist_pubid: '',
      lr_types: [
        {
          value: 1,
          title: 'Normal',
        },
        {
          value: 2,
          title: 'Bonus',
        },
        {
          value: 3,
          title: 'Event',
        },
      ],
      formValid: false, // zentraler Validierungsstatus
      rulesPositiveNumber: [
        (v) => !!v || 'Feld darf nicht leer sein',
        (v) => !Number.isNaN(v) || 'Nur Zahlen sind erlaubt',
        (v) => parseFloat(v) > 0 || 'Zahl muss positiv sein',
        (v) => parseFloat(v) <= 10000000 || 'Zahl darf höchstens 10.000.000 sein',
      ],
      rulesWith0: [
        (v) => (v !== null && (v) !== undefined) || 'Feld darf nicht leer sein',
        (v) => !Number.isNaN(v) || 'Nur Zahlen sind erlaubt',
        (v) => parseFloat(v) >= 0 || 'Zahl muss positiv sein',
        (v) => parseFloat(v) <= 10000000 || 'Zahl darf höchstens 10.000.000 sein',
      ],
      filterCategory: null,
      categories: ['Normal', 'Event', 'Bonus'],
      ErrorFlag: false,
      ErrorFlagEffect: false,
    };
  },
  mounted() {
    this.baseUrl = window.location.origin;
    BackendService.loadRuneEffects().then((data1) => {
      this.rune_effects = data1.data;
      BackendService.loadRunesGlobal().then((data) => {
        this.reward = data.data;
        // console.log(this.reward);
      });
    });
    BackendService.loadCMD_PubID({
      broadcasterid: this.$store.state.hostId,
    }).then((data) => {
      // console.log(data.data);const targetArray = data.data.find((item1) => item1.id === item.id);
      // console.log(targetArray);
      // console.log('Daten für OBS Token!');
      // console.log(data.data);
      this.cmdlist_pubid = data.data.cmdlist_pubid;
    });
  },
  computed: {
    sortedRewards() {
      const sortedArray = this.reward.slice();
      sortedArray.sort((a, b) => {
        if (a.cost !== b.cost) {
          return a.cost - b.cost;
        }
        return a.name.localeCompare(b.name);
      });

      return sortedArray;
    },
  },
  methods: {
    hasAttr1(effect) {
      return (
        effect.attribute1min !== undefined
        && effect.attribute1max !== undefined
        && effect.attribute1min !== effect.attribute1max
      );
    },
    hasAttr2(effect) {
      return (
        effect.attribute2min !== undefined
        && effect.attribute2max !== undefined
        && effect.attribute2min !== effect.attribute2max
      );
    },
    getUnit(slug, index) {
      return getUnit(this.$store.state, slug, index);
    },
    formatAttr(effect, index) {
      if (this.isCombined(effect)) {
        const unit2 = this.getUnit(effect.slug, 2);
        return `${effect.attribute1} % Chance auf ${effect.attribute2}${unit2}`;
      }

      const value = effect[`attribute${index}`];
      return `${value}${this.getUnit(effect.slug, index)}`;
    },
    getVisibleAttributes(effect) {
      // Spezialfall: luck_loyalty → nur ein kombinierter Effekt
      if (effect.slug === 'luck_loyalty') {
        return [{ index: 1, label: 'Effekt' }];
      }

      const attrs = [];

      if (this.hasAttr1(effect)) {
        attrs.push({ index: 1, label: 'Effekt' });
      }

      if (this.hasAttr2(effect)) {
        attrs.push({ index: 2, label: 'Effekt' });
      }

      return attrs;
    },
    isCombined(effect) {
      return (
        effect.attribute1min !== effect.attribute1max
        && effect.attribute2min !== effect.attribute2max
      );
    },
    onTypeChange() {
      this.selectedReward.effects = [];
    },
    removeEffect(index) {
      this.selectedReward.effects.splice(index, 1);
    },
    getAttributePercent(value, min, max) {
      if (min >= max) return 0;
      const clamped = Math.min(Math.max(value, min), max);
      return Math.round(((clamped - min) / (max - min)) * 100 * 10) / 10;
    },
    getEffectPercent(effect) {
      const p1 = this.getAttributePercent(effect.attribute1, effect.attribute1min, effect.attribute1max);
      const p2 = this.getAttributePercent(effect.attribute2, effect.attribute2min, effect.attribute2max);
      return p1 + p2;
    },
    getTotalPercent() {
      const total = this.selectedReward.effects
        .map((e) => this.getEffectPercent(e))
        .reduce((a, b) => a + b, 0);

      return Math.round(total * 10) / 10; // 1 Dezimalstelle
    },
    getRuneLevel() {
      const total = this.getTotalPercent();
      if (this.selectedReward.type === 'Event') {
        if (total > 150) {
          this.selectedReward.rarity = -1;
          return '❌ Ungültig (>150%)';
        }

        if (total >= 100) {
          this.selectedReward.rarity = 3;
          return 'Rune Stufe 3';
        }

        if (total >= 50) {
          this.selectedReward.rarity = 2;
          return 'Rune Stufe 2';
        }
      } else if (this.selectedReward.type === 'Bonus') {
        if (total > 125) {
          this.selectedReward.rarity = -1;
          return '❌ Ungültig (>125%)';
        }

        if (total >= 75) {
          this.selectedReward.rarity = 3;
          return 'Rune Stufe 3';
        }

        if (total >= 35) {
          this.selectedReward.rarity = 2;
          return 'Rune Stufe 2';
        }
      } else if (this.selectedReward.type === 'Normal') {
        if (total > 100) {
          this.selectedReward.rarity = -1;
          return '❌ Ungültig (>100%)';
        }

        if (total >= 50) {
          this.selectedReward.rarity = 3;
          return 'Rune Stufe 3';
        }

        if (total >= 25) {
          this.selectedReward.rarity = 2;
          return 'Rune Stufe 2';
        }
      }

      this.selectedReward.rarity = 1;
      return 'Rune Stufe 1';
    },
    addEffect() {
      // console.log(this.rune_effects);
      // console.log(this.selectedEffectType);
      const effect = this.rune_effects.find((e) => e.id === this.selectedEffectType);

      // this.selectedReward.effects.push({ ...this.rune_effects.find((effect) => effect.id === this.selectedEffectType) });

      const exists = this.selectedReward.effects.some((e) => e.slug === effect.slug);
      if (exists) { // optional: Feedback an den Nutzer
        console.log('Halt Stop!');
        this.ErrorFlagEffect = true;
      } else {
        this.selectedReward.effects.push({ ...effect });
      }
    },
    toggleText() {
      this.showText = !this.showText; // Text an/aus schalten
    },
    toShopView() {
      this.$router.push({
        name: 'rune_shop',
        params: { id: this.cmdlist_pubid },
      });
    },
    onFileChange(file) {
      // Fall: Datei gelöscht
      if (!file) {
        this.fileWasCleared = true;
        this.previousFile = null;
        // console.log('Datei wurde gelöscht');
        return;
      }

      this.fileWasCleared = false;

      // Fall: Erste Auswahl (keine Datei vorher)
      if (!this.fileInitiallySelected) {
        this.fileInitiallySelected = true;
        this.fileChanged = true;
        // console.log('Erste Datei ausgewählt');
      }

      // Fall: Neue Datei gewählt
      if (
        !this.previousFile
        || file.name !== this.previousFile.name
        || file.size !== this.previousFile.size
        || file.lastModified !== this.previousFile.lastModified
      ) {
        this.fileChanged = true;
        this.previousFile = file;
        // console.log('Neue Datei gewählt');
      } else {
        this.fileChanged = false;
      }
    },
    getItemImage(path) {
      if (path === null || path === '') {
        return '';
      }
      return `${process.env.VUE_APP_BACKENDSERVICE_BASEURL}img/${path}`;
    },
    getDefaultRuneImage(item) {
      const rarity = [1, 2, 3].includes(item.rarity) ? item.rarity : 1;
      return `/img/forge_rune/Runestone_${item.type}_${rarity}.png`;
    },
    openDialog(item) {
      if (item.is_bot_owner === 0) {
        this.showNotification = true;
        return;
      }
      if (item.edit === 0) {
        this.selectedReward = { ...item };
        this.showDialog = true;
        return;
      }

      this.selectedReward = { ...item };
      this.selectedReward.edit = 1;
      this.showDialog = true;
    },
    closeDialog() {
      this.showDialog = false;
    },
    saveChanges() {
      if (this.$refs.form.validate()) {
        if (this.selectedReward.id) {
          // Item bearbeiten
          console.log(this.selectedReward);
          this.loading = true;
          console.log('LOADING!!!');
          this.selectedReward.deleteCurrentImage = this.fileWasCleared;
          this.selectedReward.changeCurrentImage = this.fileChanged;
          this.fileWasCleared = false;
          this.fileChanged = false;
          BackendService.saveRunesGlobal(this.selectedReward)
            .then((data) => {
              // Aktualisiere den Store mit der Mutationsmethode
              console.log(data.data);
              // this.reward = data.data.rewards;
              this.showDialog = false;
            }).catch((error) => {
              // console.log(error.response.status);
              // console.log(error.response.data);
              if (error.response.status === 409 && error.response.data === 'user_not_connected_with_streamer_dc') {
                this.dynamicErrorMsg = 'Der angegebene User ist nicht mit dem Streamer DC verbunden!';
                this.errorReedemReward = true;
              } else if (error.response.status === 409 && error.response.data === 'streamer_not_connected_with_dc_bot') {
                this.dynamicErrorMsg = 'Streamer benutzt aktuell den DC-Bot nicht!';
                this.errorReedemReward = true;
              } else if (error.response.status === 409 && error.response.data === 'twitchuser_not_found') {
                this.dynamicErrorMsg = 'Der angegebene Twitchuser wurde nicht gefunden!';
                this.errorReedemReward = true;
              }
            }).finally(() => {
              this.loading = false;
            });
        } else {
          // Neues Item hinzufügen
          this.loading = true;
          this.fileWasCleared = false;
          this.fileChanged = false;
          this.soundChanged = false;
          this.soundWasCleared = false;
          console.log(this.selectedReward);
          BackendService.createRuneGlobal(this.selectedReward)
            .then((data) => {
              this.reward = data.data;
              this.showDialog = false;
            }).catch((error) => {
              // console.log(error.response.status);
              // console.log(error.response.data);
              if (error.response.status === 409 && error.response.data === 'user_not_connected_with_streamer_dc') {
                this.dynamicErrorMsg = 'Der angegebene User ist nicht mit dem Streamer DC verbunden!';
                this.errorReedemReward = true;
              } else if (error.response.status === 409 && error.response.data === 'streamer_not_connected_with_dc_bot') {
                this.dynamicErrorMsg = 'Streamer benutzt aktuell den DC-Bot nicht!';
                this.errorReedemReward = true;
              } else if (error.response.status === 409 && error.response.data === 'twitchuser_not_found') {
                this.dynamicErrorMsg = 'Der angegebene Twitchuser wurde nicht gefunden!';
                this.errorReedemReward = true;
              }
            }).finally(() => {
              this.loading = false;
            });
        }
      } else this.ErrorFlag = true;
    },
    saveSwitchState(item) {
      if (item.is_paused) {
        this.selectedReward.is_paused = 1;
      } else {
        this.selectedReward.is_paused = 0;
      }
      if (item.is_vip) {
        this.selectedReward.is_vip = 1;
      } else {
        this.selectedReward.is_vip = 0;
      }
      if (item.is_enabled) {
        this.selectedReward.is_enabled = 1;
      } else {
        this.selectedReward.is_enabled = 0;
      }
      if (item.is_user_input_required) {
        this.selectedReward.is_user_input_required = 1;
      } else {
        this.selectedReward.is_user_input_required = 0;
      }
      if (item.max_per_user_per_stream_is_enabled) {
        this.selectedReward.max_per_user_per_stream_is_enabled = 1;
      } else {
        this.selectedReward.max_per_user_per_stream_is_enabled = 0;
      }
      if (item.max_per_stream_is_enabled) {
        this.selectedReward.max_per_stream_is_enabled = 1;
      } else {
        this.selectedReward.max_per_stream_is_enabled = 0;
      }
      if (item.should_redemptions_skip_request_queue) {
        this.selectedReward.should_redemptions_skip_request_queue = 1;
      } else {
        this.selectedReward.should_redemptions_skip_request_queue = 0;
      }
      if (item.chat_msg) {
        this.selectedReward.chat_msg = 1;
      } else {
        this.selectedReward.chat_msg = 0;
      }
    },
    deleteReward() {
      // console.log(this.selectedReward.id);
      this.dialogDelete = true;
    },
    deleteItemConfirm() {
      // console.log(this.selectedReward.id);
      // console.log('LÖSCHEN!!!');
      BackendService.deleteRuneGlobal(this.selectedReward).then((data) => {
        this.dialogDelete = false;
        this.showDialog = false;
        this.reward = data.data;
      });
    },
    closeDelete() {
      // console.log(this.selectedReward.id);
      // console.log('NICHT LÖSCHEN!!!');
      this.dialogDelete = false;
    },
  },
};
</script>
<style scoped>
html, body {
  margin: 0;
  padding: 0;
  background: transparent;
  overflow: auto; /* verhindert Scrollbars in OBS */
}

.channelPointsWrapper {
  top: 0;
  left: 0;
  width: 100%;
  min-height: 100%;
  box-sizing: border-box;
  padding-bottom: 30px;
  padding-left: 20px;
  padding-right: 20px;
  background: transparent;
  margin: 0;

  /* Klicks standardmäßig durchlassen */
  pointer-events: none;

}

.channelPointsWrapper * {
  /* Interaktive Elemente in den Channel Points wieder aktiv machen */
  pointer-events: auto;
}

.rune-card {
  width: 100%;
}

.rune-card-image {
  width: 64px;
  height: 64px;
  object-fit: contain;
  flex-shrink: 0;
}

@media (max-width: 599px) {
  .rune-card-image {
    width: 48px;
    height: 48px;
  }
}

</style>
